import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './ReceptionClient.css';

const STATUT_CONFIG = {
  recu:     { label: 'Reçu',      color: '#3b82f6', bg: '#eff6ff' },
  en_cours: { label: 'En cours',  color: '#f59e0b', bg: '#fffbeb' },
  traite:   { label: 'Traité',    color: '#10b981', bg: '#f0fdf4' },
  archive:  { label: 'Archivé',   color: '#6b7280', bg: '#f9fafb' },
};

const MOTIF_LBL = {
  revision: 'Révision', diagnostic: 'Diagnostic',
  reparation: 'Réparation', autre: 'Autre',
};

function StatusBadge({ statut }) {
  const cfg = STATUT_CONFIG[statut] || STATUT_CONFIG.recu;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px',
      borderRadius: '12px', fontSize: '12px', fontWeight: '600',
      color: cfg.color, background: cfg.bg,
      border: `1px solid ${cfg.color}33`,
    }}>{cfg.label}</span>
  );
}

// ── Photos lightbox ────────────────────────────────────────────────────────────
function PhotosModal({ photos, title, onClose }) {
  const [idx, setIdx] = useState(0);
  const list = (photos || []).filter(Boolean);
  if (!list.length) return null;

  return (
    <div className="rc-lb-overlay" onClick={onClose}>
      <div className="rc-lb-box" onClick={e => e.stopPropagation()}>
        <div className="rc-lb-header">
          <span>{title} — {idx + 1} / {list.length}</span>
          <button className="rc-lb-close" onClick={onClose}>✕</button>
        </div>
        <div className="rc-lb-img-wrap">
          <img src={`data:image/jpeg;base64,${list[idx]}`} alt={`Photo ${idx+1}`} className="rc-lb-img" />
        </div>
        {list.length > 1 && (
          <div className="rc-lb-nav">
            <button onClick={() => setIdx(i => (i - 1 + list.length) % list.length)}>‹</button>
            <span>{list.map((_, i) => (
              <span key={i} className={`rc-lb-dot ${i === idx ? 'active' : ''}`}
                    onClick={() => setIdx(i)} />
            ))}</span>
            <button onClick={() => setIdx(i => (i + 1) % list.length)}>›</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Détail modal ───────────────────────────────────────────────────────────────
function DetailModal({ dossier, isSuperAdmin, onClose, onUpdated, onDeleted }) {
  const [statut, setStatut]     = useState(dossier.statut);
  const [notes, setNotes]       = useState(dossier.notes || '');
  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [photos, setPhotos]     = useState(null);
  const [photoModal, setPhotoModal] = useState(null); // { list, title }

  useEffect(() => {
    api.get(`/reception-client/${dossier.id}/photos`)
      .then(r => setPhotos(r.data))
      .catch(() => {});
  }, [dossier.id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/reception-client/${dossier.id}`, { statut, notes });
      onUpdated(r.data.dossier);
      onClose();
    } catch { alert('Erreur lors de la mise à jour'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer définitivement ce dossier ?')) return;
    setDeleting(true);
    try {
      await api.delete(`/reception-client/${dossier.id}`);
      onDeleted(dossier.id);
      onClose();
    } catch { alert('Erreur lors de la suppression'); }
    finally { setDeleting(false); }
  };

  const d = dossier;
  const isPr = d.type === 'prereception';

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <span className="rc-modal-type">{isPr ? '📋 Pré-réception' : '🔑 Dépôt du jour'}</span>
            <h2 className="rc-modal-title">{d.plaque || '—'}</h2>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {/* Client */}
          <div className="rc-section">
            <div className="rc-section-title">Client</div>
            <div className="rc-grid2">
              <div><div className="rc-lbl">Nom</div><div className="rc-val">{d.client_prenom} {(d.client_nom||'').toUpperCase()}</div></div>
              <div><div className="rc-lbl">Téléphone</div><div className="rc-val">{d.client_telephone || '—'}</div></div>
            </div>
          </div>

          {/* Véhicule */}
          <div className="rc-section">
            <div className="rc-section-title">Véhicule</div>
            <div className="rc-grid2">
              <div>
                <div className="rc-lbl">Plaque</div>
                <div className="rc-val"><span className="plaque-badge">{d.plaque || '—'}</span></div>
              </div>
              {d.marque && <div><div className="rc-lbl">Marque</div><div className="rc-val">{d.marque}</div></div>}
            </div>
          </div>

          {/* Pré-réception specifics */}
          {isPr && (
            <>
              {d.motif && (
                <div className="rc-section">
                  <div className="rc-section-title">Motif</div>
                  <div className="rc-val">{MOTIF_LBL[d.motif] || d.motif}</div>
                </div>
              )}
              {d.description && (
                <div className="rc-section">
                  <div className="rc-section-title">Description</div>
                  <div className="rc-val rc-desc">{d.description}</div>
                </div>
              )}
              {d.options_demandees && d.options_demandees.length > 0 && (
                <div className="rc-section">
                  <div className="rc-section-title">Points à contrôler</div>
                  <div className="rc-tags">
                    {d.options_demandees.map(o => <span key={o} className="rc-tag">{o}</span>)}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Dépôt specifics */}
          {!isPr && (
            <div className="rc-section">
              <div className="rc-section-title">Informations véhicule</div>
              <div className="rc-grid3">
                <div>
                  <div className="rc-lbl">Kilométrage</div>
                  <div className="rc-val">{d.kilometrage ? d.kilometrage.toLocaleString('fr-FR') + ' km' : '—'}</div>
                </div>
                <div>
                  <div className="rc-lbl">Niveau d'essence</div>
                  <div className="rc-val">{d.niveau_essence != null ? d.niveau_essence + ' %' : '—'}</div>
                </div>
                <div>
                  <div className="rc-lbl">Voyants</div>
                  <div className="rc-val">
                    {d.voyants && d.voyants.length > 0
                      ? d.voyants.map((v, i) => <span key={i} className="rc-tag rc-tag-warn">{v}</span>)
                      : <span style={{color:'var(--text-secondary)'}}>Aucun</span>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Localisation GPS (dépôt uniquement) */}
          {!isPr && d.latitude && d.longitude && (
            <div className="rc-section">
              <div className="rc-section-title">Localisation GPS</div>
              <iframe
                className="rc-map-iframe"
                title="Carte localisation"
                src={`https://maps.google.com/maps?q=${d.latitude},${d.longitude}&t=k&z=18&output=embed`}
                loading="lazy"
              />
            </div>
          )}

          {/* Photos */}
          {photos && (
            <div className="rc-section">
              <div className="rc-section-title">Photos</div>
              <div className="rc-photos-row">
                {photos.photo_plaque && (
                  <button className="rc-photo-btn"
                    onClick={() => setPhotoModal({ list: [photos.photo_plaque], title: 'Plaque' })}>
                    🔍 Plaque
                  </button>
                )}
                {isPr && photos.photos_vehicule && photos.photos_vehicule.length > 0 && (
                  <button className="rc-photo-btn"
                    onClick={() => setPhotoModal({ list: photos.photos_vehicule, title: 'Photos client' })}>
                    📷 Photos ({photos.photos_vehicule.length})
                  </button>
                )}
                {!isPr && photos.photos_tour && photos.photos_tour.length > 0 && (
                  <button className="rc-photo-btn"
                    onClick={() => setPhotoModal({ list: photos.photos_tour, title: 'Tour du véhicule' })}>
                    🚘 Tour ({photos.photos_tour.length})
                  </button>
                )}
                {!isPr && photos.photo_tableau_bord && (
                  <button className="rc-photo-btn"
                    onClick={() => setPhotoModal({ list: [photos.photo_tableau_bord], title: 'Tableau de bord' })}>
                    📊 Tableau de bord
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Statut + notes */}
          <div className="rc-section">
            <div className="rc-section-title">Gestion</div>
            <div className="rc-field">
              <label>Statut</label>
              <select value={statut} onChange={e => setStatut(e.target.value)}>
                {Object.entries(STATUT_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div className="rc-field" style={{ marginTop: 10 }}>
              <label>Notes internes</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)}
                        rows={3} placeholder="Ajoutez une note…" />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          {isSuperAdmin && (
            <button className="btn-danger" onClick={handleDelete} disabled={deleting || saving}
                    style={{ marginRight: 'auto' }}>
              {deleting ? 'Suppression…' : '🗑 Supprimer'}
            </button>
          )}
          <button className="rc-btn-cancel" onClick={onClose}>Fermer</button>
          <button className="rc-btn-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement…' : '💾 Enregistrer'}
          </button>
        </div>
      </div>

      {photoModal && (
        <PhotosModal photos={photoModal.list} title={photoModal.title}
                     onClose={() => setPhotoModal(null)} />
      )}
    </div>
  );
}

// ── Composant principal ────────────────────────────────────────────────────────
export default function ReceptionClient({ user, setHeaderAction }) {
  const isSuperAdmin = user?.role === 'super-admin';

  const [tab, setTab]         = useState('prereception'); // 'prereception' | 'depot_jour'
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');
  const [statutFilter, setStatutFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [toast, setToast]     = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type: tab };
      if (statutFilter) params.statut = statutFilter;
      if (search.trim()) params.search = search.trim();
      const r = await api.get('/reception-client', { params });
      setDossiers(r.data.dossiers || []);
    } catch { showToast('Erreur chargement', 'error'); }
    finally { setLoading(false); }
  }, [tab, statutFilter, search]);

  useEffect(() => { load(); }, [load]);

  // Pas de bouton d'action dans le header pour cette page

  const handleUpdated = (updated) => {
    setDossiers(prev => prev.map(d => d.id === updated.id ? { ...d, ...updated } : d));
  };
  const handleDeleted = (id) => setDossiers(prev => prev.filter(d => d.id !== id));

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';

  return (
    <div className="page-container">
      {toast && <div className={`rc-toast ${toast.type}`}>{toast.msg}</div>}

      {/* Tabs */}
      <div className="page-tabs">
        <button className={tab === 'prereception' ? 'active' : ''}
                onClick={() => { setTab('prereception'); setSearch(''); setStatutFilter(''); }}>
          📋 Pré-réceptions
          {dossiers.length > 0 && tab === 'prereception' && <span className="tab-count">{dossiers.length}</span>}
        </button>
        <button className={tab === 'depot_jour' ? 'active' : ''}
                onClick={() => { setTab('depot_jour'); setSearch(''); setStatutFilter(''); }}>
          🔑 Réceptions du jour
          {dossiers.length > 0 && tab === 'depot_jour' && <span className="tab-count">{dossiers.length}</span>}
        </button>
      </div>

      {/* Toolbar */}
      <div className="page-toolbar">
        <input className="page-search" type="text" placeholder="🔍 Rechercher..."
               value={search} onChange={e => setSearch(e.target.value)} />
        <select className={`page-select${statutFilter ? ' active-filter' : ''}`} value={statutFilter} onChange={e => setStatutFilter(e.target.value)}>
          <option value="">Tous statuts</option>
          {Object.entries(STATUT_CONFIG).map(([k,v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="table-container">
        {loading ? (
          <div className="rc-loading">Chargement…</div>
        ) : dossiers.length === 0 ? (
          <div className="rc-empty">Aucun dossier</div>
        ) : (
          <table className="rc-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Plaque</th>
                <th>Client</th>
                <th>Téléphone</th>
                {tab === 'prereception' && <th>Motif</th>}
                {tab === 'depot_jour'   && <th>Kilométrage</th>}
                {tab === 'depot_jour'   && <th>Essence</th>}
                {tab === 'depot_jour'   && <th>Voyants</th>}
                <th>Statut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {dossiers.map(d => (
                <tr key={d.id} onClick={() => setSelected(d)} style={{ cursor: 'pointer' }}>
                  <td className="rc-date">{fmtDate(d.created_at)}</td>
                  <td><span className="plaque-badge">{d.plaque || '—'}</span></td>
                  <td>{d.client_prenom} {(d.client_nom||'').toUpperCase()}</td>
                  <td>{d.client_telephone || '—'}</td>
                  {tab === 'prereception' && <td>{MOTIF_LBL[d.motif] || d.motif || '—'}</td>}
                  {tab === 'depot_jour'   && (
                    <td>{d.kilometrage ? d.kilometrage.toLocaleString('fr-FR') + ' km' : '—'}</td>
                  )}
                  {tab === 'depot_jour'   && <td>{d.niveau_essence != null ? d.niveau_essence + ' %' : '—'}</td>}
                  {tab === 'depot_jour'   && (
                    <td>
                      {d.voyants && d.voyants.length > 0
                        ? <span className="rc-tag-warn-sm" title={d.voyants.join(', ')}>⚠️ {d.voyants.length}</span>
                        : <span style={{color:'var(--text-muted)'}}>—</span>}
                    </td>
                  )}
                  <td><StatusBadge statut={d.statut} /></td>
                  <td onClick={e => e.stopPropagation()}>
                    <button className="rc-btn-open" onClick={() => setSelected(d)} title="Ouvrir">⤢</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <DetailModal
          dossier={selected}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setSelected(null)}
          onUpdated={handleUpdated}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
}
